import { Fragment } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ChevronRight } from "lucide-react";
import { EXTRAS, STEPS } from "@/lib/content";
import { MaskLines, Reveal } from "@/components/site/Motion";
import { Icon } from "@/components/kit/Icon";

export function JourneySteps({ showLink = true, showExtras = false }: { showLink?: boolean; showExtras?: boolean }) {
  return (
    <section data-testid="steps-section" className="grain relative overflow-hidden bg-olive py-24 text-cream sm:py-32">
      <div className="wrap relative">
        <div className="max-w-3xl">
          <p className="eyebrow-dark">How it works</p>
          <h2 className="mt-5 text-5xl leading-[0.98] sm:text-6xl">
            <MaskLines lines={["A simple, supportive journey", <em key="h" className="text-gold-soft">to a healthier you.</em>]} />
          </h2>
          <Reveal><p className="mt-6 max-w-lg text-base leading-relaxed text-cream/85">Our 4-step process is designed to make your wellness journey easy, personalised and sustainable.</p></Reveal>
        </div>
        <ol className="mt-16 grid gap-10 sm:grid-cols-2 lg:flex lg:items-start lg:gap-0">
          {STEPS.map((s, i) => (
            <Fragment key={s.num}>
              <Reveal delay={i * 0.12} className="lg:flex-1">
                <li data-testid={`step-${s.num}`} className="group flex flex-col items-start text-left lg:items-center lg:px-4 lg:text-center">
                  <span className="relative">
                    <span className="icon-ring h-20 w-20 bg-olive-deep transition-colors duration-500 group-hover:bg-gold group-hover:text-forest-deep sm:h-24 sm:w-24"><Icon name={s.icon} className="h-8 w-8" /></span>
                    <span className="absolute -right-1 -top-1 grid h-8 w-8 place-items-center rounded-full bg-gold font-display text-lg text-forest-deep">{s.num}</span>
                  </span>
                  <h3 className="mt-6 font-display text-3xl">{s.title}</h3>
                  <p className="mt-3 max-w-[16rem] text-sm leading-relaxed text-cream/80">{s.text}</p>
                </li>
              </Reveal>
              {i < STEPS.length - 1 && <ChevronRight aria-hidden="true" className="mt-9 hidden h-6 w-6 shrink-0 text-gold-soft/70 lg:block" />}
            </Fragment>
          ))}
        </ol>
        {showLink && (
          <Reveal className="mt-14">
            <Link to="/how-it-works" data-testid="steps-learn-more-link" className="btn-cream">See the full process <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        )}
        {showExtras && (
          <div className="mt-20 grid items-center gap-8 lg:grid-cols-12" data-testid="steps-extras">
            <Reveal className="lg:col-span-6">
              <div className="aspect-[4/3] overflow-hidden rounded-[28px]" style={{ clipPath: "polygon(0 4%, 100% 0, 100% 96%, 0 100%)" }}>
                <img src="/images/journal.jpg" alt="Writing wellness goals in a journal" loading="lazy" className="h-full w-full object-cover" />
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-6">
              <div className="rounded-[28px] bg-beige-soft p-8 text-charcoal sm:p-10">
                <h3 className="font-display text-4xl text-forest">You'll also get</h3>
                <ul className="mt-6 space-y-4">
                  {EXTRAS.map((x) => <li key={x} className="flex items-center gap-3 text-base"><CheckCircle2 className="h-5 w-5 shrink-0 text-pine" />{x}</li>)}
                </ul>
              </div>
            </Reveal>
          </div>
        )}
      </div>
    </section>
  );
}
